import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import { faEnvelope, faSpinner, faEye, faExclamationTriangle, faCheckCircle, faClock, faPaperPlane, faSearch, faXmark } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, shortObjectID } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { type Mail, type MailStatus, type MailTemplate, type UserID, ALL_MAIL_STATUSES, ALL_MAIL_TEMPLATES, MAIL_TEMPLATE_LABELS } from '@/models';

const pageSize = 50;

const inputClassName = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent';

interface MailsTableProps {
  userId?: UserID;
  reloadKey?: number;
  onTotalChange?: (total: number) => void;
}

// datetime-local values carry no timezone; new Date() interprets them as local time
const localDateTimeToRFC3339 = (value: string): string | undefined => {
  if (value === '') {
    return undefined;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
};

const getStatusIcon = (status: MailStatus) => {
  switch (status) {
    case 'WAITING':
      return <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-yellow-600" />;
    case 'SENDING':
      return <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4 text-blue-600 animate-pulse" />;
    case 'SUCCESS':
      return <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-600" />;
    case 'FAILED':
      return <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-600" />;
    default:
      return null;
  }
};

const getStatusColor = (status: MailStatus) => {
  switch (status) {
    case 'WAITING':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
    case 'SENDING':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
    case 'SUCCESS':
      return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
    case 'FAILED':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
  }
};

export default function MailsTable({ userId, reloadKey = 0, onTotalChange }: MailsTableProps) {
  const [mails, setMails] = useState<Mail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [statusFilter, setStatusFilter] = useState<MailStatus | 'ALL'>('ALL');
  const [templateFilter, setTemplateFilter] = useState<MailTemplate | 'ALL'>('ALL');
  const [receiverSearch, setReceiverSearch] = useState('');
  const [debouncedReceiverSearch, setDebouncedReceiverSearch] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  const requestSeq = useRef(0);
  const onTotalChangeRef = useRef(onTotalChange);
  onTotalChangeRef.current = onTotalChange;

  const hasActiveFilters = statusFilter !== 'ALL' || templateFilter !== 'ALL' || receiverSearch !== '' || createdFrom !== '' || createdTo !== '';

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = receiverSearch.trim();
      if (trimmed !== debouncedReceiverSearch) {
        setDebouncedReceiverSearch(trimmed);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [receiverSearch, debouncedReceiverSearch]);

  const withPageReset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setCurrentPage(1);
  };

  const fetchMails = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      setError(null);

      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listMails({
        limit: pageSize,
        next: cursorToken,
        userID: userId,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        template: templateFilter === 'ALL' ? undefined : templateFilter,
        receiver: debouncedReceiverSearch !== '' ? debouncedReceiverSearch : undefined,
        since: localDateTimeToRFC3339(createdFrom),
        until: localDateTimeToRFC3339(createdTo),
      });
      if (seq !== requestSeq.current) {
        return;
      }
      setMails(response.mails);
      setTotal(response.total);
      onTotalChangeRef.current?.(response.total);
    } catch (err) {
      if (seq !== requestSeq.current) {
        return;
      }
      console.error('Error loading mails list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der E-Mails');
      handleAPIError(err);
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [currentPage, userId, statusFilter, templateFilter, debouncedReceiverSearch, createdFrom, createdTo]);

  useEffect(() => {
    fetchMails();
  }, [fetchMails, reloadKey]);

  const resetFilters = () => {
    setStatusFilter('ALL');
    setTemplateFilter('ALL');
    setReceiverSearch('');
    setDebouncedReceiverSearch('');
    setCreatedFrom('');
    setCreatedTo('');
    setCurrentPage(1);
  };

  return (
    <>
      {/* Filter section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-4 self-center">Status:</span>
            {(['ALL', ...ALL_MAIL_STATUSES] as const).map((status) => (
              <button
                key={status}
                onClick={() => withPageReset(setStatusFilter)(status)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {status === 'ALL' ? 'Alle' : status}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {userId === undefined && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Empfänger
                </label>
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="E-Mail-Adresse..."
                    value={receiverSearch}
                    onChange={(e) => setReceiverSearch(e.target.value)}
                    className={`${inputClassName} pl-10 pr-10`}
                  />
                  {receiverSearch !== '' && (
                    <button
                      onClick={() => setReceiverSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                    >
                      <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template
              </label>
              <select
                value={templateFilter}
                onChange={(e) => withPageReset(setTemplateFilter)(e.target.value as MailTemplate | 'ALL')}
                className={`${inputClassName} cursor-pointer`}
              >
                <option value="ALL">Alle Templates</option>
                {ALL_MAIL_TEMPLATES.map((template) => (
                  <option key={template} value={template}>
                    {MAIL_TEMPLATE_LABELS[template]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Erstellt von
              </label>
              <input
                type="datetime-local"
                value={createdFrom}
                max={createdTo !== '' ? createdTo : undefined}
                onChange={(e) => withPageReset(setCreatedFrom)(e.target.value)}
                className={inputClassName}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Erstellt bis
              </label>
              <input
                type="datetime-local"
                value={createdTo}
                min={createdFrom !== '' ? createdFrom : undefined}
                onChange={(e) => withPageReset(setCreatedTo)(e.target.value)}
                className={inputClassName}
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div>
              <button
                onClick={resetFilters}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center cursor-pointer"
              >
                <FontAwesomeIcon icon={faXmark} className="w-4 h-4 mr-1" />
                Filter zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error !== null && !loading && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6 flex items-center justify-between">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchMails}
            className="text-sm font-medium text-red-700 dark:text-red-300 hover:underline cursor-pointer"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {mails.length > 0 && !loading && error === null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Empfänger
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Betreff
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Mail-ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {mails.map((mail) => (
                  <tr key={mail.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(mail.status)}`}>
                        {getStatusIcon(mail.status)}
                        <span className="ml-1">{mail.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {mail.receiverEmail}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {mail.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {MAIL_TEMPLATE_LABELS[mail.template] ?? mail.template}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {shortObjectID(mail.id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(mail.created)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <Link
                        to={`/admin/mails/${mail.id}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center"
                      >
                        <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-1" />
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}

      {mails.length === 0 && !loading && error === null && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faEnvelope} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Keine E-Mails gefunden</p>
        </div>
      )}
    </>
  );
}
